// SPDX-License-Identifier: BUSL-1.1

/**
 * Shared API response types for the UI.
 *
 * These mirror @cli-ceo/core types but use string dates (JSON serialization).
 *
 */

export interface ConfigSurface {
  key: string;
  name: string;
  description?: string;
  environments: string[];
  createdAt: string;
}

export interface ConfigVersion {
  id: string;
  surfaceKey: string;
  version: number;
  config: unknown;
  environment: string;
  status: 'draft' | 'staged' | 'promoted' | 'rolled_back';
  createdBy: string;
  createdAt: string;
  promotedAt?: string;
}

export interface Flag {
  key: string;
  enabled: boolean;
  description?: string;
  provider?: string;
  configType?: string;
  lifecycleStage?: string;
  rolloutPercentage?: number;
  owner?: string;
  updatedAt: string;
}

/** Flag provider → badge classes. */
export const PROVIDER_COLORS: Record<string, string> = {
  internal: 'bg-neutral-700 text-neutral-300',
  posthog: 'bg-orange-900/30 text-orange-400',
  launchdarkly: 'bg-indigo-900/30 text-indigo-400',
  env_var: 'bg-cyan-900/30 text-cyan-400',
};

/** Flag config type display labels. */
export const CONFIG_TYPE_LABELS: Record<string, string> = {
  feature_flag: 'Feature',
  kill_switch: 'Kill Switch',
  experiment: 'Experiment',
};

/** Lifecycle stage → badge classes. */
export const LIFECYCLE_COLORS: Record<string, string> = {
  draft: 'bg-neutral-700 text-neutral-300',
  rollout: 'bg-yellow-900/40 text-yellow-400',
  graduated: 'bg-green-900/30 text-green-400',
  sunset: 'bg-red-900/30 text-red-400',
};

export interface AuditEntry {
  id: string;
  actorId: string;
  actorType: 'human' | 'agent' | 'system';
  action: string;
  resourceType: string;
  resourceId: string;
  createdAt: string;
}

export interface AuditResponse {
  entries: AuditEntry[];
  total: number;
  cursor?: string;
}

/** Config lifecycle status → Tailwind classes. */
export const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-neutral-700 text-neutral-300',
  staged: 'bg-yellow-900/40 text-yellow-400',
  promoted: 'bg-green-900/30 text-green-400',
  rolled_back: 'bg-red-900/30 text-red-400',
};

/** Audit action → text color. Matches on action suffix (e.g., 'config.promote' → 'promote'). */
export const ACTION_COLORS: Record<string, string> = {
  promote: 'text-green-400',
  rollback: 'text-red-400',
  create: 'text-blue-400',
  stage: 'text-yellow-400',
  toggle: 'text-yellow-400',
  kill_switch: 'text-red-400',
  invalidate: 'text-orange-400',
};

/** Actor type → badge classes. */
export const ACTOR_BADGE: Record<string, string> = {
  human: 'bg-blue-900/30 text-blue-400',
  agent: 'bg-purple-900/30 text-purple-400',
  system: 'bg-neutral-800 text-neutral-400',
};

/** Extract the action suffix for color lookup (e.g., 'config.promote' → 'promote'). */
export function actionSuffix(action: string): string {
  return action.split('.').pop() ?? action;
}

/* ── Agent / Session types ─────────────────────────── */

/**
 * Status vocabulary note (DEBT-17):
 *
 * Agents and sessions use DIFFERENT strings for their "broken" terminal state:
 * - Agents use `'error'` (never `'failed'`)
 * - Sessions use `'failed'` (never `'error'`)
 *
 * This is historical vocabulary drift between two subsystems, not a bug. The
 * discriminated unions below make the distinction compile-time enforced so
 * you can't accidentally write `agents.filter(a => a.status === 'failed')`
 * and silently get zero results.
 *
 * When adding a new agent/session status, update the `as const` array below
 * so the union picks it up automatically.
 */

/** All valid agent statuses. Enforced by `AgentStatus` + `AGENT_STATUS_COLORS`. */
export const AGENT_STATUSES = ['active', 'idle', 'offline', 'error'] as const;
export type AgentStatus = (typeof AGENT_STATUSES)[number];

/**
 * All valid session statuses. Matches Anthropic Managed Agents transport:
 * see `packages/adapters/src/managed-agents/types.ts` SessionStatus.
 *
 * - `idle`: session created, not yet running
 * - `running`: actively executing
 * - `rescheduling`: being resumed/restarted by MA
 * - `terminated`: ended (success or failure — MA does not distinguish)
 *
 * NOTE: `'completed'` and `'failed'` are NOT valid MA session statuses.
 * The previous UI type used those; this was dead code because MA never
 * returned them. Session outcomes are derived from usage + stats, not status.
 */
export const SESSION_STATUSES = [
  'idle',
  'running',
  'rescheduling',
  'terminated',
] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export interface Agent {
  id: string;
  name: string;
  status: AgentStatus;
  model?: string;
  environmentId?: string;
  createdAt: string;
}

/**
 * Self-reported runtime attestation from the agent process (PIO-31).
 * Populated on every heartbeat by cli-ceo-agents runners on PR #14+.
 * Pre-PIO-31 runners send no attestation — this is null in that case.
 */
export interface Attestation {
  repoSha: string | null;
  repoOrigin: string | null;
  capabilitiesVersion: string;
  secretsHash: string | null;
  bootedAtMs: number;
  nodeVersion: string;
}

/** Response shape from GET /api/governance/agents/:agentId/attestation */
export interface AgentAttestationResponse {
  agentId: string;
  attestation: Attestation | null;
  lastBootedAt: string | null;
  lastHeartbeat: string | null;
}

/** Response shape from GET /api/governance/agent-drift?expectedSha=... */
export interface AgentDriftResponse {
  expectedSha: string;
  drifted: Array<{
    agentId: string;
    runningSha: string | null;
    capabilitiesVersion: string | null;
    bootedAtMs: number | null;
    lastHeartbeat: string | null;
  }>;
  synced: Array<{ agentId: string; runningSha: string }>;
  unknown: Array<{ agentId: string; lastHeartbeat: string | null }>;
  summary: { total: number; drifted: number; synced: number; unknown: number };
}

// ---------------------------------------------------------------------------
// Status surfaces (ADR 008 — schema-driven status surface vocabulary).
// Mirrors packages/core/src/governance/surface-types.ts.
// ---------------------------------------------------------------------------

/** Severity ladder per ADR 008. Critical > Degraded > Watch > Healthy. Unknown is orthogonal. */
export type Severity = 'critical' | 'degraded' | 'watch' | 'healthy' | 'unknown';

/** Brand-vocabulary term a surface participates in. `null` for surfaces without a clean mapping. */
export type SurfaceBrandTerm =
  | 'EVIDENCE_CHAIN'
  | 'EPISTEMIC_DRIFT'
  | 'INTENT_BINDING'
  | 'AUTHORIZED_POSTURE'
  | 'POSTURE_TRANSITION'
  | null;

export type SurfaceType =
  | 'enum_state'
  | 'boolean_flag'
  | 'freshness_age'
  | 'version_drift'
  | 'ratio_pct'
  | 'budget_remaining'
  | 'queue_depth'
  | 'compliance_expiry'
  | 'latency_pct'
  | 'error_rate'
  | 'counter_rate';

/** Action affordance authorized for the calling user's posture. Null if read-only. */
export interface SurfaceAffordance {
  kind: string;
  label: string;
  url: string;
  arming: 'none' | 'hold' | 'type';
}

/** Server-projected surface — already filtered by posture. Renderers consume directly. */
export interface ProjectedSurface {
  type: SurfaceType;
  label: string;
  brandTerm: SurfaceBrandTerm;
  severity: Severity;
  /** Pre-rendered human-readable state (e.g. "$4.92 of $5.00", "4s ago"). */
  valueLabel: string;
  /** Drilldown link to the audit log row / JSONL trace / intent binding that produced this state. */
  evidenceUrl: string;
  affordance: SurfaceAffordance | null;
  renderHint: 'compact' | 'full' | 'inline';
}

/** Response shape from GET /api/agents/:agentId/surfaces (ADR 008 Phase 2). */
export interface AgentSurfaceResponse {
  agentId: string;
  rollup: {
    severity: Severity;
    hasUnknown: boolean;
    label: string;
  };
  surfaces: ProjectedSurface[];
  identity: {
    agentId: string;
    sdk: string;
    name: string;
    version: string | null;
    organizationId: string | null;
  };
  asOf: string;
}

export interface Session {
  id: string;
  sessionId?: string;  // Internal session ID from API
  agentId: string;
  status: SessionStatus;
  title?: string;
  startedAt?: string;
  endedAt?: string | null;
  durationSeconds?: number | null;  // Calculated on session end
  durationHuman?: string | null;    // Formatted duration (e.g., "4m 32s")
  activeSeconds?: number;          // Alias for durationSeconds (UI compatibility)
  inputTokens: number;
  outputTokens: number;
  cost?: string;
  createdAt: string;
  updatedAt: string;
}

/** Agent status → badge classes. Typed so missing statuses fail at compile time. */
export const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  active: 'bg-green-900/30 text-green-400',
  idle: 'bg-yellow-900/40 text-yellow-400',
  offline: 'bg-neutral-700 text-neutral-300',
  error: 'bg-red-900/30 text-red-400',
};

/** Session status → badge classes. Typed so missing statuses fail at compile time. */
export const SESSION_STATUS_COLORS: Record<SessionStatus, string> = {
  idle: 'bg-yellow-900/40 text-yellow-400',
  running: 'bg-green-900/30 text-green-400',
  rescheduling: 'bg-blue-900/30 text-blue-400',
  terminated: 'bg-neutral-700 text-neutral-300',
};

/* ── Moderation types ──────────────────────────────── */

export const POLICY_GATES = ['safe', 'guarded', 'manual'] as const;
export type PolicyGate = (typeof POLICY_GATES)[number];

export const MODERATION_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type ModerationStatus = (typeof MODERATION_STATUSES)[number];

export interface ModerationItem {
  id: string;
  sessionId: string;
  toolUseId: string;
  toolName: string;
  toolInput: unknown;
  policyGate: PolicyGate;
  status: ModerationStatus;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  denyMessage?: string;
}

/** Policy gate → badge classes. */
export const GATE_COLORS: Record<PolicyGate, string> = {
  safe: 'bg-green-900/30 text-green-400',
  guarded: 'bg-yellow-900/40 text-yellow-400',
  manual: 'bg-red-900/30 text-red-400',
};

/** Moderation status → badge classes. */
export const MODERATION_STATUS_COLORS: Record<ModerationStatus, string> = {
  pending: 'bg-yellow-900/40 text-yellow-400',
  approved: 'bg-green-900/30 text-green-400',
  rejected: 'bg-red-900/30 text-red-400',
};

/* ── pi.dev Agent types ───────────────────────────── */

export interface PiDevAgent {
  id: string;
  agent_id: string;
  name: string;
  source: string;
  status: PiDevAgentStatus;
  policy: PolicyGate;
  model?: string;
  budget: {
    monthly?: number;
    per_session?: number;
    action: 'warn' | 'block' | 'terminate';
  };
  last_heartbeat?: string;
  created_at: string;
  updated_at: string;
}

export type PiDevAgentStatus = 'idle' | 'running' | 'paused' | 'terminated';

export const PI_DEV_AGENT_STATUS_COLORS: Record<PiDevAgentStatus, string> = {
  idle: 'bg-yellow-900/40 text-yellow-400',
  running: 'bg-green-900/30 text-green-400',
  paused: 'bg-blue-900/30 text-blue-400',
  terminated: 'bg-neutral-700 text-neutral-300',
};

export interface PiDevSession {
  id: string;
  session_id: string;
  agent_id: string;
  status: PiDevSessionStatus;
  started_at: string;
  ended_at?: string;
  input_tokens: number;
  output_tokens: number;
  cost: number;
  tool_call_count: number;
}

/**
 * Pi.dev session lifecycle. **Intentionally distinct from `SessionStatus`**
 * (the canonical managed-agents session vocabulary): pi.dev sessions are
 * vendor-managed and emit `'active' | 'completed' | 'terminated'` directly
 * from the upstream API. Mapping these back to canonical
 * `'idle' | 'running' | 'rescheduling' | 'terminated'` would lose the
 * `completed` distinction (success vs. abnormal termination), which the
 * pi-dev UI depends on for color coding.
 *
 * `ts-004` exempts `PiDevSessionStatus`. Do not change to canonical
 * vocabulary without first updating the upstream pi.dev integration AND
 * the ts-004 rule together.
 */
export type PiDevSessionStatus = 'active' | 'completed' | 'terminated';

export const PI_DEV_SESSION_STATUS_COLORS: Record<PiDevSessionStatus, string> = {
  active: 'bg-green-900/30 text-green-400',
  completed: 'bg-neutral-700 text-neutral-300',
  terminated: 'bg-red-900/30 text-red-400',
};

export interface PiDevBudget {
  agent_id: string;
  period: 'monthly' | 'session';
  used: number;
  limit: number;
  remaining: number | null;
  percent_used: number | null;
}

/* ── pi Session execution metadata (PIO-01) ─────────── */

/**
 * Structured execution metadata emitted by the pi CLI runner for a single job.
 *
 * Persisted into `ops_agent_jobs.output_payload.piSession` and surfaced via
 * GET /api/agents/:agentId/jobs.
 *
 * Older runners may have omitted some fields, so everything except the core
 * identity/billing tuple is optional.
 */
export interface PiSession {
  /** UUIDv7 identifier the runner assigns to the pi session. */
  sessionId: string;
  /** Model identifier as reported by the runner (e.g. `MiniMax-M2.7-highspeed`). */
  model: string;
  /** Provider key — used for the badge color (`minimax`, `anthropic`, `openai`, …). */
  provider: string;
  /** Total tokens billed by the provider (input + output + cache). */
  totalTokens: number;
  /** Prompt tokens sent to the model. */
  inputTokens?: number;
  /** Completion tokens returned by the model. */
  outputTokens?: number;
  /** Cache-read tokens (charged at a discount on most providers). */
  cacheReadTokens?: number;
  /** Cache-write tokens (charged at a premium when the cache is populated). */
  cacheWriteTokens?: number;
  /** Cost as reported by pi — may be 0 for self-hosted/custom providers. */
  costUsd?: number;
  /** Cost computed locally from per-token rates — authoritative when `costUsd` is 0. */
  estimatedCostUsd?: number;
  /** Number of agent turns (model invocations) performed in the session. */
  turnCount?: number;
  /** Number of tool calls executed during the session. */
  toolCallCount?: number;
  /** Names of tools the agent invoked, in invocation order. */
  toolNames?: string[];
  /** Path to the JSONL session transcript on the runner host (PIO-09 will turn this into a download). */
  sessionFilePath?: string;
  /** Reason the session ended (`stop`, `error`, `length`, `tool_use`, …). */
  stopReason?: string;
}

/**
 * Shape of `ops_agent_jobs.output_payload` as returned by GET /api/agents/:agentId/jobs.
 *
 * This is a loose contract — different runners write different fields. The
 * index signature lets us narrow incrementally without breaking older payloads.
 */
export interface OutputPayload {
  /** Markdown-formatted findings the agent produced (e.g. PR review summary). */
  findings?: string;
  /** Captured stdout / runner logs. */
  logs?: string;
  /** Pi-reported cost in USD (may be 0 for self-hosted providers — see `piSession.estimatedCostUsd`). */
  cost?: number;
  /** Wall-clock duration of the job in milliseconds. */
  duration?: number;
  /** Git diff or file snapshot captured at job completion. */
  codeSnapshot?: string;
  /** ISO-8601 timestamp of when the runner marked the job done. */
  completedAt?: string;
  /** Structured pi CLI execution metadata (PIO-01). */
  piSession?: PiSession;
  /** Allow vendor-specific fields without losing type safety on the known ones. */
  [key: string]: unknown;
}
